import {useCallback, useEffect, useState} from 'react';
import {useGetHealthDatesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthDatesQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useQueryParams} from 'presentation/pages';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {
	AuthModal,
	HandleHourSelectionParams,
	UseTelehealthProfessionalSelectionProps,
} from './types';

import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {useProviderNamesStore} from 'presentation/modules/booking/shared/store/useProviderNamesStore';
import getFilteredOptions from 'presentation/modules/booking/shared/utils/getFilteredOptions';
import {useTelehealthSearchBar} from 'presentation/modules/booking/telehealth/ui/components/TelehealthSearchBar/hooks/useTelehealthSearchBar';
import {useTelehealthBooking} from '../../../../hooks/useTelehealthBooking';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';
import {ErrorsAuthorization} from 'presentation/modules/booking/shared/constants/errorsAuthorizationEnum';
import {useDisabledSelectionDateStore} from 'presentation/modules/booking/shared/store/useDisabledSelectionDateStore';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {TherapyFlagEnum} from 'presentation/constants';

export function useTelehealthProfessionalSelection({
	handleNextStep,
}: UseTelehealthProfessionalSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const getProviderNamesState = useProviderNamesStore(state => state.providersName);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const setDisabledSelectionDate = useDisabledSelectionDateStore(
		state => state.setDisabledSelectionDate,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const [authModal, setAuthModal] = useState<AuthModal>('none');
	const {
		SEARCH_PARAMS: {calendarActive},
	} = useQueryParams();
	const queryClient = useQueryClient();
	const {dateAt, isRebooking} = bookingData;

	const isCalendarActive = !!(calendarActive && calendarActive === 'true');
	const shouldUseUniqueDate = Boolean(isCalendarActive && dateAt);

	const showSelectedDateProfessionalGrid = shouldUseUniqueDate;

	const {activeBookingFlowStep} = useTelehealthBooking();
	const {handleClearShiftAndSearch} = useTelehealthSearchBar({activeStep: activeBookingFlowStep});

	const {
		data: availableDates,
		isFetching: isFetchingAvailableDates,
		error: errorAvailableDates,
		refetch: availableDatesRefetch,
	} = useGetHealthDatesQuery({enabled: true, isTelemedicine: true});
	const {verifyHealthDateAuthorization} = useBookingAuthorization();

	const hasNoAvailableDates =
		!isFetchingAvailableDates && (!availableDates || availableDates.length === 0);

	const notFoundAvailableDatesError = isUnprocessableError(errorAvailableDates);

	const isLoading = isFetchingAvailableDates;
	const isError = errorAvailableDates && !notFoundAvailableDatesError;

	const showNotFoundState = hasNoAvailableDates || notFoundAvailableDatesError;
	const shouldHideSearchBar = !!errorAvailableDates || hasNoAvailableDates;

	const flTipo = isRebooking ? TherapyFlagEnum.REBOOKING : TherapyFlagEnum.BOOKING;

	const refetch = useCallback(() => {
		return availableDatesRefetch();
	}, [availableDatesRefetch]);

	const providersNameOptions =
		getProviderNamesState?.medicos?.map(({cdPrestadorFisico, nmMedico}) => ({
			id: cdPrestadorFisico,
			label: nmMedico,
		})) || [];

	const filteredProvidersNameOptions = getFilteredOptions(
		providersNameOptions,
		currentValueInputTypingState,
	);

	const handleSelectHour = async ({
		profCode,
		profName,
		date,
		hour,
	}: HandleHourSelectionParams) => {
		setBookingData(prev => ({
			...prev,
			professionalCode: profCode,
			professionalName: profName,
			dateAt: date,
			hourAt: hour,
		}));

		const canGoToNextStep = await checkAuthorization(date);
		if (canGoToNextStep) {
			handleNextStep();
		}
	};

	async function checkAuthorization(date: string) {
		if (bookingData.multipleBookings && !bookingData.isRebooking) {
			try {
				setAuthModal('loading');
				const response = await verifyHealthDateAuthorization({
					flTipo,
					healthSelectedDate: date,
					healthSelectedSpecialty: bookingData.specialtyCode,
				});

				if (response.cdRetorno === ErrorsAuthorization.NEED_AUTHORIZATION) {
					setAuthModal('needAth');
					return false;
				}

				if (response.cdRetorno === ErrorsAuthorization.UNAVAILABLE_SLOTS) {
					setAuthModal('allScheduled');
					return false;
				}

				if (response.cdRetorno === ErrorsAuthorization.MINIMUM_INTERVAL) {
					setAuthModal('minimumInterval');
					return false;
				}
				setAuthModal('none');
				return true;
			} catch (error) {
				return setAuthModal('error');
			}
		}
		return true;
	}

	const resetAvailableTimesAndDates = () => {
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.HEALTH_AVAILABLE_TIMES],
		});
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.HEALTH_DATES],
		});
	};

	useEffect(() => {
		setDisabledSelectionDate(authModal !== 'none');
	}, [authModal, setDisabledSelectionDate]);

	useEffect(() => {
		setHideSearchBarState(shouldHideSearchBar);
	}, [shouldHideSearchBar, setHideSearchBarState]);

	useEffect(() => {
		resetAvailableTimesAndDates();
	}, []);

	return {
		refetch,
		isLoading,
		isError,
		showNotFoundState,
		handleSelectHour,
		showSelectedDateProfessionalGrid,
		handleClearShiftAndSearch,
		availableDates,
		filteredProvidersNameOptions,
		authModal,
		setAuthModal,
		setExternalOpenDateModal,
	};
}
